import { useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { TextField } from '@/shared/ui/Field';

import { useRequestPasswordReset } from '../api/passwordReset';
import { deliveryOf } from '../model/reset';

/* Asking for a reset link. One field, and one answer whatever the address.
 *
 * **The confirmation must not say whether the account exists, and that is the whole security
 * property of this screen.** The route answers 202 for a known and an unknown address alike — the
 * same reason `/login` has one refusal message for a wrong password, an unknown account and a
 * disabled one — and copy that said "we have sent you a link" would undo the API's work in the one
 * place a reader could tell the two apart. So the sentence is conditional: a link is on its way *if*
 * that address has an account. It reads slightly awkwardly, and that awkwardness is the feature.
 *
 * **`transport: "none"` is the one thing this screen may state plainly**, because it is a fact about
 * the deployment rather than about the address. A deployment with no mail transport accepts the
 * request and can deliver nothing, so a silent success there is the worst outcome available: somebody
 * refreshing an inbox for a mail nobody can send. It names the administrator as the next step.
 *
 * The form stays on screen after a successful request rather than being replaced by the
 * confirmation. A mistyped address is the likeliest reason somebody is here twice, and a screen that
 * hides the box makes correcting one a navigation.
 */
export function ForgotPasswordForm(): ReactElement {
  const { t } = useTranslation();
  const request = useRequestPasswordReset();
  const [email, setEmail] = useState('');

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (email.trim() === '') return;
    request.mutate({ email: email.trim() });
  };

  /* Read from the mutation's own data rather than held in state, so a second submission with a
   * different address cannot leave the first answer on screen underneath it. */
  const delivery = request.isSuccess ? deliveryOf(request.data.transport) : null;

  return (
    <>
      <h1 className="lq-auth__heading">{t('auth.forgot.title')}</h1>
      <p className="lq-form-note">{t('auth.forgot.lede')}</p>

      <form onSubmit={onSubmit}>
        <TextField
          label={t('auth.field.email')}
          type="text"
          value={email}
          onChange={setEmail}
          required
        />

        {delivery === 'sent' && (
          /* `notice`, not `error` or `warning`: nothing went wrong and nothing needs deciding. */
          <Alert tone="notice" title={t('auth.forgot.sentTitle')}>
            {t('auth.forgot.sentBody')}
          </Alert>
        )}

        {delivery === 'undeliverable' && (
          /* **A warning rather than an error, and the distinction is whose fault it is.** The
             request was accepted and the platform is working as configured; what is missing is a
             mail transport, which only an operator can supply. An error would read as a fault the
             reader could retry their way out of. */
          <Alert tone="warning" title={t('auth.forgot.noTransportTitle')}>
            {t('auth.forgot.noTransportBody')}
          </Alert>
        )}

        {request.isError && (
          /* The route is a 202 by construction, so reaching here means the request never landed —
             an unreachable API, a proxy, a rate limit. The server's own `detail` says which. */
          <Alert tone="error" title={t('auth.forgot.failedTitle')}>
            {request.error.message}
          </Alert>
        )}

        <Button type="submit" tone="primary" size="block" busy={request.isPending}>
          {t('auth.forgot.submit')}
        </Button>
      </form>

      <p className="lq-dim lq-auth__aside">
        <Link to={SCREEN_PATHS.login}>{t('auth.forgot.backToLogin')}</Link>
      </p>
    </>
  );
}
